import { useEffect, useRef, useState } from "react";
import { track } from "../analytics";
import type { MediaItem } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { userApi } from "../lib/api";
import { useFapsStore } from "../lib/faps";
import { AuthModal } from "../control/components/AuthModal";
import { CrownMark } from "../control/ui/Crown";
import type { FapBus, FapBusFn } from "./types";
import { useCrownBursts } from "../lib/useCrownBursts";
import { useTileScale } from "./useTileScale";

function sameClip(a: MediaItem | null, b: MediaItem | null): boolean {
  if (!a || !b) return a === b;
  return a.id === b.id && a.sourceKind === b.sourceKind;
}

/**
 * Persistent "fap" affordance for a single region on the view page: a translucent
 * crown pinned to the region's bottom-right corner that fills gold once the clip on
 * screen has been fapped, plus a floating-crown burst layer. The crown is a
 * spammable "like": every click adds the clip to your library (idempotent — never
 * un-faps) and launches one more rising crown, so rapid taps stack bursts. It is the
 * ONLY fap trigger — the region itself means something else entirely: a mouse click
 * opens that region's feed popup, and on a finger a tap selects the tile, a second tap
 * lets it go, and a double tap opens the popup — though hosts can still route a gesture
 * in via `fapBus`.
 */
/**
 * Did this event land on the fap crown?
 *
 * THE ONLY PLACE A CROWN TAP CAN BE KEPT OFF THE TILE. React does not listen for
 * mouseenter — it builds enter from native mouseover, gathers handlers walking UP from
 * the node entered, and plays that queue outermost-first, so the tile's onMouseEnter runs
 * BEFORE the crown's ever would. The crown therefore cannot stopPropagation its way out
 * of being a selection: by the time anything of its own fires, `hovered` has already
 * moved. And that synthetic mouseenter is precisely what selects a tile under a finger,
 * so pressing the crown selected the region no matter what the crown did with its click.
 *
 * The enter event's `target` is the deepest node entered, which is the evidence this
 * asks for. The same predicate keeps a crown tap out of the tap record at pointerdown —
 * the crown's click never reaches the tile, so a record written there would sit stale and
 * be read by whatever tap came next. Lives beside the marker it reads, and is shared by
 * every stage that puts a crown on a tile.
 */
export const crownHit = (t: EventTarget | null): boolean =>
  t instanceof Element && t.closest("[data-fap-crown]") !== null;

/** "Have I fapped this clip, and how many people have?" — remembered per clip across every
 *  region and every advance, for ten minutes. Module-level on purpose: sixteen overlays on
 *  one wall share one memory, and a clip that wraps around comes back with its answer
 *  instead of a GET. Written by the lookup and by the crown itself (a tap IS the answer). */
const FAP_MEMO_MS = 10 * 60_000;
const fapMemo = new Map<string, { fapped: boolean; count: number; at: number }>();

/** The count under the crown: 7, 950, 5.3K, 53K, 1M, 1.2M. About four characters fit.
 *  Rounded down, so it never claims more faps than there are. */
export function crownCount(n: number): string {
  const short = (v: number, unit: string) => {
    // One decimal below ten (5.3K, 1.2M), none above it (53K); a bare ".0" is dropped (1M).
    const rounded = v < 10 ? Math.floor(v * 10) / 10 : Math.floor(v);
    return `${rounded}${unit}`;
  };
  if (n < 1000) return String(n);
  if (n < 1_000_000) return short(n / 1000, "K");
  return short(n / 1_000_000, "M");
}

export function RegionFapOverlay({
  regionId,
  surface = "screen",
  getCurrent,
  signedIn,
  fapBus,
  onFapped,
}: {
  /** Which surface the fap is counted on — the screen's regions, or a scroll's. */
  surface?: "screen" | "infinity";
  regionId: string;
  getCurrent: () => MediaItem | null;
  /**
   * Whether there is an account behind this browser. Signed-out taps open the sign-up
   * dialog instead of fapping.
   *
   * Deliberately NOT "can this be fapped": that depends on the clip ON SCREEN, which
   * changes every few seconds as the region advances, and the host cannot answer it —
   * the host holds the registry in a ref, so a clip change re-renders nothing up there
   * and any boolean it computed would be frozen at whatever was playing when it last
   * rendered. On a wall square, which mixes catalog picks and a buyer's uploads in one
   * rotation, that frozen answer is wrong half the time in both directions: a crown that
   * signs an already-signed-in visitor in, or one that offers an act the server refuses.
   * The clip-dependent half is decided below, off the same `current` the crown follows.
   */
  signedIn: boolean;
  fapBus: FapBus;
  /** This tile was fapped here, by the person at this screen — a room relays it. */
  onFapped?: (regionId: string) => void;
}) {
  // Track the clip on screen (it changes as the region advances) so the crown
  // follows the current clip.
  const getCurrentRef = useRef(getCurrent);
  getCurrentRef.current = getCurrent;
  const [current, setCurrent] = useState<MediaItem | null>(() => getCurrent());
  const [fapped, setFapped] = useState(false);
  // How many people have fapped the clip on screen. Shown under the crown, never when 0.
  const [count, setCount] = useState(0);
  const { bursts, pushBurst } = useCrownBursts();
  const [authOpen, setAuthOpen] = useState(false);
  // The crown grows with its tile, by the same rule the hover chrome grows by — a
  // half-television region deserves more than a 40px target in its corner. The rising
  // bursts scale through CrownMark's size prop instead of a transform: fapFloat's
  // keyframes own `transform` on the animated div, and a scale there would be clobbered
  // on the animation's first frame.
  const crownRef = useRef<HTMLButtonElement | null>(null);
  const tileScale = useTileScale(crownRef);

  useEffect(() => {
    const tick = () =>
      setCurrent((prev) => {
        const c = getCurrentRef.current();
        return sameClip(prev, c) ? prev : c;
      });
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // On each new clip, seed the crown from the server: whether this account fapped it, and
  // how many people have. The count is asked for signed-out viewers too now (it is the
  // same GET, which answers without an account). A wall repeats its clips constantly, so
  // the answer is memoised: one lookup per distinct clip per FAP_MEMO_MS, not one per
  // region per advance, which is what kept the old every-advance lookup's traffic down
  // (2026-09-10). An upload has no count to ask for, so it never asks.
  const curKey = current ? clipKey(current) : null;
  const memoKey = curKey ? `${signedIn ? "in" : "out"}|${curKey}` : null;
  useEffect(() => {
    if (!current || current.sourceKind === "upload") {
      setFapped(false);
      setCount(0);
      return;
    }
    const known = fapMemo.get(memoKey!);
    if (known && Date.now() - known.at < FAP_MEMO_MS) {
      setFapped(signedIn && known.fapped);
      setCount(known.count);
      return;
    }
    setFapped(false);
    setCount(0);
    let cancelled = false;
    userApi
      .fapStatus(current)
      .then((s) => {
        fapMemo.set(memoKey!, { fapped: s.fapped, count: s.count, at: Date.now() });
        if (!cancelled) {
          setFapped(signedIn && s.fapped);
          setCount(s.count);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [memoKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * A wall UPLOAD is never fappable — its clip token deliberately resolves to nothing
   * server-side (see clip-ids.ts), so a crown on one would fill, fire a 400 the client
   * swallows, and un-fill on the next tick. Read off `current`, which the interval above
   * keeps pointed at the clip actually on screen, so a square that mixes uploads with
   * catalog picks gets the right answer on every advance rather than once per host render.
   */
  const fappable = !!current && current.sourceKind !== "upload";

  // Spammable fap: always add-to-library + burst, never un-fap. Fire-and-forget.
  // Signed-out taps open the sign-up dialog instead.
  function fapAndBurst() {
    if (!current) return;
    // Guarded here as well as on `disabled`, because fapBus routes a host gesture
    // straight in without going through the button at all.
    if (!fappable) return;
    if (!signedIn) {
      setAuthOpen(true);
      return;
    }
    // A first fap from this account is one more person; another tap is the same person.
    const nextCount = fapped ? count : count + 1;
    setFapped(true);
    setCount(nextCount);
    // The tap is the answer too: the next region to land this clip need not ask.
    if (memoKey) fapMemo.set(memoKey, { fapped: true, count: nextCount, at: Date.now() });
    // The reply carries the balance when the tap paid (under the day's cap): the pill
    // ticks, nothing floats. The store is reached through getState, not a hook: sixteen
    // overlays subscribing to the wallet would re-render a whole wall per balance tick.
    userApi
      .fapClick(current)
      .then((r) => {
        // The whole reply, paid or not: the store reads `capped` off a tap that paid nothing.
        useFapsStore.getState().applyClick(r);
      })
      .catch(() => undefined);
    track("fap_saved", { surface: surface });
    pushBurst();
    onFapped?.(regionId);
  }

  // Keep a ref to the latest fapAndBurst so the fn registered in the bus (once, on
  // mount) always sees the current clip.
  const fapAndBurstRef = useRef(fapAndBurst);
  fapAndBurstRef.current = fapAndBurst;
  // The burst alone, for a fap relayed from someone else in a room: the crown rises
  // here, but this clip is not fapped and nothing is sent — that would echo forever.
  const pushBurstRef = useRef(pushBurst);
  pushBurstRef.current = pushBurst;
  useEffect(() => {
    const fn: FapBusFn = (o) => (o?.burstOnly ? pushBurstRef.current() : fapAndBurstRef.current());
    fapBus.set(regionId, fn);
    return () => {
      fapBus.delete(regionId);
    };
  }, [regionId, fapBus]);

  return (
    <>
      {/* Fap crown (bottom-right) — spammable, with how many people have fapped the clip */}
      <button
        type="button"
        ref={crownRef}
        /*
         * THE MARKER THE TILE READS, and the reason stopping the click was never enough.
         * What selects a tile under a FINGER is the tap's own synthetic mouseenter, and
         * React builds enter from native mouseover and dispatches it ANCESTOR-FIRST — the
         * tile's handler has already run by the time this button's would, so nothing this
         * button does to its own events can call that selection off. The tile asks who was
         * really entered instead, off this attribute.
         *
         * Deliberately NOT an onPointerDown stopPropagation to go with the click one:
         * React's stopPropagation stops the NATIVE event too, and the master audio panel
         * dismisses on a document-level pointerdown — pressing the crown would stop being
         * able to close it.
         */
        data-fap-crown=""
        style={{ transform: `scale(${tileScale})`, transformOrigin: "bottom right" }}
        // Both stopped: the region underneath opens its feed popup on a click, and
        // pressing the crown must fap WITHOUT popping an editor over the clip you were
        // looking at. The button is a fap and nothing else. The blur() drops focus so
        // the next Space skips the wall instead of being swallowed by this button as a
        // synthesized click — one more crown when the whole wall was told to move on.
        onClick={(e) => {
          e.stopPropagation();
          e.currentTarget.blur();
          fapAndBurst();
        }}
        onDoubleClick={(e) => e.stopPropagation()}
        disabled={!fappable}
        aria-pressed={fapped}
        title={
          !fappable
            ? "This square's own upload — it can't be saved"
            : signedIn
              ? "Fap — save to your library"
              : "Sign up to fap"
        }
        className={
          "absolute bottom-3 right-3 z-30 flex h-10 w-10 items-center justify-center rounded-full backdrop-blur transition-all duration-150 hover:opacity-100 disabled:opacity-40 " +
          (fapped ? "bg-[#f5c518]/25 opacity-100" : "bg-black/45 opacity-70")
        }
      >
        <CrownMark
          size={20}
          tone={fapped ? "gold" : "muted"}
          className="drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]"
        />
        {/* Out of flow, so the crown stays exactly where it was in the circle's middle. It
            scales with the tile because the whole button does (the transform above). */}
        {count > 0 ? (
          <span
            aria-label={`${count} ${count === 1 ? "fap" : "faps"}`}
            className={
              "pointer-events-none absolute inset-x-0 bottom-[2px] text-center text-[7px] font-semibold leading-none tabular-nums drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)] " +
              (fapped ? "text-[#f5c518]" : "text-white/90")
            }
          >
            {crownCount(count)}
          </span>
        ) : null}
      </button>

      {/* Floating-crown bursts, rising from the crown's corner */}
      <div className="pointer-events-none absolute inset-0 z-40">
        {bursts.map((id) => (
          <div
            key={id}
            className="absolute bottom-3 right-3 animate-[fapFloat_0.9s_ease-out_forwards]"
          >
            <CrownMark
              size={Math.round(28 * tileScale)}
              className="drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]"
            />
          </div>
        ))}
      </div>

      {authOpen && (
        <AuthModal
          initialMode="register"
          reason="Create a free account to fap this clip."
          onClose={() => setAuthOpen(false)}
        />
      )}
    </>
  );
}
